/* eslint-disable no-param-reassign */
/* eslint-disable consistent-return */
/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
import CryptoJS from 'crypto-js';
import moment from 'moment';
import { useCallback, useEffect, useState } from 'react';
import { logout } from '~/services/auth';
import { getAuditHeaders } from '~/utils/auditCorrelation';
import Constants from '~/utils/constants';
import { CustomizedError } from '~/utils/errors';

export enum ApiMethod {
  GET = 'GET',
  POST = 'POST',
  PUT = 'PUT',
  DELETE = 'DELETE',
  PATCH = 'PATCH',
}

type UseApiParams<T> =
  | {
      url: string;
      method?: ApiMethod.GET;
      body?: object;
      tryNewWay?: boolean;
      headers?: object;
      multipart?: boolean;
      fetchOnMount?: boolean;
      enabled?: boolean;
      onFinished?: never;
    }
  | {
      url: string;
      method: Exclude<ApiMethod, ApiMethod.GET>;
      body?: object;
      tryNewWay?: boolean;
      headers?: object;
      multipart?: boolean;
      fetchOnMount?: boolean;
      enabled?: boolean;
      onFinished?: (
        response: Pick<
          ApiResponse<T>,
          'data' | 'error' | 'response' | 'status' | 'mensagem'
        >,
      ) => void;
    };

type ApiResponse<T> = {
  status: number | null;
  loading: boolean;
  error: string | null;
  data: T | null;
  success: boolean;
  response: Response | null;
  mensagem: string | null;
  isFetched?: boolean;
};
const responseApiInitialState = {
  status: null,
  loading: false,
  error: null,
  data: null,
  success: false,
  response: null,
  mensagem: null,
};
function useApi<T>(
  urlOrParams: string | UseApiParams<T>,
  method?: ApiMethod,
  body: object = {},
  tryNewWay = false,
  headers = {},
  multipart = false,
) {
  const params: UseApiParams<T> =
    typeof urlOrParams === 'string'
      ? ({
          url: urlOrParams,
          method,
          body,
          tryNewWay,
          headers,
          multipart,
          fetchOnMount: false,
          enabled: true,
        } as UseApiParams<T>)
      : {
          ...urlOrParams,
          tryNewWay: true,
          enabled: urlOrParams.enabled !== false,
        };

  const {
    url,
    method: _method = ApiMethod.GET,
    body: _body = {},
    tryNewWay: _tryNewWay,
    headers: _headers = {},
    multipart: _multipart = false,
    fetchOnMount = params.enabled ?? false,
    onFinished = null,
  } = params;

  const [responseApi, setResponseApi] = useState<ApiResponse<T>>(
    responseApiInitialState,
  );
  const callApi = (reqBody = {}) => FetchData(reqBody);

  useEffect(() => {
    if (fetchOnMount) {
      _body ? callApi(_body) : callApi();
    }
  }, [fetchOnMount, JSON.stringify(_body)]);

  const refetch = (reqBody?: {} | null) => {
    setResponseApi({
      ...responseApi,
      loading: true,
      error: null,
      data: null,
      success: false,
      response: null,
      mensagem: null,
    });

    return reqBody ? callApi(reqBody) : _body ? callApi(_body) : callApi();
  };
  const getFormData = (object) => {
    return Object.keys(object).reduce((formData, key) => {
      formData.append(key, object[key]);
      return formData;
    }, new FormData());
  };

  const getOptions = (reqBody = {}) => {
    const reqHeaders: Headers = new Headers();
    const token = localStorage.getItem(Constants.TOKEN_KEY);
    const tokenBearer = localStorage.getItem(Constants.TOKEN_BEARER);
    const ipAdress = localStorage.getItem(Constants.IP_ADRESS);
    const { userAgent } = navigator;
    const sessionId = localStorage.getItem(Constants.SESSION_ID);
    const xCode = localStorage.getItem(Constants.X_CODE);
    const idOperator = localStorage.getItem(Constants.USER_ID);
    const tokenReCaptcha = sessionStorage.getItem(Constants.TOKEN_RECAPTCHA);

    const handleCryptAES = (value: string): string => {
      const aleatorio = (Math.random() + 1).toString(36).substr(2, 8);
      const date = new Date();
      const now_utc = Date.UTC(
        date.getUTCFullYear(),
        date.getUTCMonth(),
        date.getUTCDate(),
        date.getUTCHours(),
        date.getUTCMinutes(),
        date.getUTCSeconds(),
      );

      const encrypted = CryptoJS.AES.encrypt(
        value,
        CryptoJS.enc.Latin1.parse('cBmT7UMdFQkL4ckQUeFU^pULW$Yo8!h^'),
        {
          iv: CryptoJS.enc.Latin1.parse(
            moment.utc(now_utc).format('MMYYYYDD') + aleatorio,
          ),
          padding: CryptoJS.pad.Pkcs7,
          mode: CryptoJS.mode.CBC,
        },
      );

      return aleatorio + encrypted.toString();
    };

    if (sessionId) reqHeaders.set('Id-Sessao', sessionId);
    reqHeaders.set('Id-Sistema', `${process.env.REACT_APP_ID_SISTEMA}`);
    Object.entries(getAuditHeaders()).forEach(([name, value]) =>
      reqHeaders.set(name, value),
    );
    if (userAgent) reqHeaders.set('User-Agent', userAgent);
    if (xCode) reqHeaders.set('X-Code', xCode);
    if (ipAdress) reqHeaders.set('X-Sent-By', ipAdress);
    if (idOperator) reqHeaders.set('Id-User', idOperator);
    if (idOperator)
      reqHeaders.set('X-Id-Usuario-Criptografado', handleCryptAES(idOperator));
    reqHeaders.set('Content-Type', 'application/json');
    if (token) reqHeaders.set('apikey', `token ${token}`);
    if (tokenBearer) reqHeaders.set('Authorization', `Bearer ${tokenBearer}`);
    if (tokenReCaptcha) reqHeaders.set('ReCaptcha-Token', tokenReCaptcha);

    if (!_multipart) {
      reqHeaders.set('Content-Type', 'application/json');
    }

    if (_headers) Object.assign(reqHeaders, _headers);
    let newBody = reqBody || _body;

    if (!Array.isArray(_body) && !Array.isArray(reqBody)) {
      newBody = Object.assign(_body, reqBody);
    }
    let options: RequestInit = {
      method: _method,
      headers: reqHeaders,
    };
    if (_method !== ApiMethod.GET) {
      if (_multipart) {
        options = {
          ...options,
          body: getFormData(newBody),
        };
        delete options.headers['Content-Type'];
      } else {
        options = {
          ...options,
          body: JSON.stringify(newBody) || null,
        };
      }
    }
    return options;
  };

  const FetchData = useCallback(
    async (
      reqBody = {},
    ): Promise<{
      status: number;
      data: object & T;
      response: Response;
      mensagem?: string;
      success: boolean;
    }> => {
      setResponseApi({ ...responseApi, loading: true });

      const options = getOptions(reqBody);
      let copyResponse;
      let json = null;
      let newUrl = url;
      let res: Response;
      try {
        // transforma um objeto em query params no metodo GET
        if (options.method === ApiMethod.GET && _tryNewWay) {
          // exclui parametros vazios, nulos ou undefined
          Object.keys(reqBody).forEach((e) => {
            if (
              reqBody[e] === '' ||
              reqBody[e] === undefined ||
              reqBody[e] === null
            ) {
              delete reqBody[e];
            }
          });
          const bodyParams = new URLSearchParams(reqBody).toString();
          newUrl = `${url}?${bodyParams}`;
          res = await fetch(newUrl, options);
        } else {
          res = await fetch(url, options);
        }
        copyResponse = res.clone();
        const statusResponse = res.status;

        // A propria chamada de login pode retornar 401 (ex.: fora do horario funcional) - nesse
        // caso nao existe sessao alguma para encerrar, e o SignIn ja trata o erro exibindo
        // response.mensagem no dialog dele. Sem essa exclusao, os dois fluxos disparavam juntos:
        // o dialog do SignIn E o redirect/dialog do logout, com a mesma mensagem duplicada.
        const isLoginRequest = url.includes('/auth/login');

        if (res.status === 401 && !isLoginRequest) {
          try {
            const errorBody = await res.clone().json();
            if (errorBody?.mensagem) {
              sessionStorage.setItem(Constants.SESSAO_ENCERRADA_MENSAGEM, errorBody.mensagem);
            }
          } catch (e) {
            // corpo do 401 pode nao ser JSON (ex.: erro de rede/proxy) - segue sem mensagem customizada
          }
          logout();
          // logout() so agenda o reload (window.location.href) - nao interrompe o JS que continua
          // rodando ate o navegador efetivamente descarregar a pagina. Se deixassemos o fluxo
          // seguir daqui, quem chamou essa funcao receberia a resposta de erro e mostraria o
          // proprio dialog (varias telas fazem isso) ANTES do F5 acontecer, e so depois viria o
          // dialog da tela de login lendo o sessionStorage - dois modais brigando com o reload no
          // meio. Retornar uma Promise que nunca resolve trava esse fluxo aqui: nada mais executa
          // nesta chamada, o F5 forcado acontece primeiro, e o unico dialog que aparece e o da
          // tela de login, depois do reload.
          return new Promise<never>(() => {});
        }

        if (res.status === 403) {
          throw CustomizedError({
            message: 'Você não possui permissão para prosseguir.',
          });
        }

        const contentType = res.headers.get('content-type');
        if (contentType && contentType.indexOf('application/json') !== -1) {
          json = await res.json();
        }

        if (
          (contentType && contentType.indexOf('text/csv;charset=UTF-8')) !== -1
        ) {
          json = await res.blob();
        }

        if (
          contentType &&
          contentType.indexOf('application/vnd.ms-excel') !== -1
        ) {
          json = await res.blob();
        }
        const ok = res.ok;

        if (options.method !== ApiMethod.GET) {
          if (onFinished) {
            onFinished({
              status: statusResponse,
              data: json as T,
              response: res,
              mensagem: json ? json.mensagem : '',
              error: ok ? null : json?.mensagem || json?.message || 'Erro desconhecido',
            });
          }
        }

        setResponseApi({
          loading: false,
          data: json,
          error: ok ? null : json?.mensagem || json?.message || 'Erro desconhecido',
          status: statusResponse,
          success: true,
          response: res,
          mensagem: json ? json.mensagem : '',
          isFetched: true,
        });
      } catch (err) {
        setResponseApi({
          loading: false,
          data: null,
          error: err.message,
          status: copyResponse?.status,
          success: false,
          response: null,
          mensagem: null,
          isFetched: true,
        });
      }

      return {
        status: copyResponse?.status,
        success: copyResponse.ok,
        data: json,
        response: copyResponse,
        mensagem: json ? json.mensagem : '',
      };
    },
    [url, _method, _body, _tryNewWay, _multipart, _headers],
  );
  const clear = () => {
    setResponseApi(responseApiInitialState);
  };
  return { callApi, refetch, clear, ...responseApi };
}

export default useApi;
